import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { get } from 'lodash'
import PromisePool from '@supercharge/promise-pool'
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';

import TeacherSelections from '../TeacherSelections'
import Button from "../../blocks/Button";
import Certificate from '../Badging/Certificate';
import './index.scss'
import Dropdown from '../../../Global/Dropdown';
import { BADGE_TYPE, REWARD_LEVEL, REWARD_LEVEL_OPTIONS, LOCALE_OPTIONS } from '../../../../services/badging/constants';
import getTeacherName from '../../util/getTeacherName';
import CertificatePrintWrapper, { printCertificates } from '../Badging/CertificatePrintWrapper';
import { createBadge } from '../../../../services/badging/createBadge/createBadge';
import { getDomoEmbedSrc } from '../../../../services/Domo';
import PrintModal from '../Badging/PrintModal';

// Badging Report's Certificate Builder
export default function BadgeBuilder(props) {
  const [rewardLevelId, setRewardLevelId] = useState(REWARD_LEVEL.PAL);
  const [description, setDescription] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [domoEmbedSrc, setDomoEmbedSrc] = useState(null);
  const [classname, setClassname] = useState("");
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [locale, setLocale] = useState("EN_US");

  const teacherName = getTeacherName();

  const roster = useSelector(state => state.reports.roster);
  const { students, isClassBadge } = useSelector(state => state.reports.badging);
  const redirect = students.length === 0;

  const {t} = useTranslation("badging_report")

  // initialize classname input based on selected class in roster
  useEffect(() => {
    if (!roster || !isClassBadge) return;
    const initClassname = get(roster, 'ui.selections.classroom.label').substring(0, 24);
    setClassname(initClassname);
  }, [roster])

  useEffect(() => {
    if (redirect) props.history.push("/teacher/reports/type/badging");
  }, [redirect]);

  useEffect(() => {
    if (!classId) return;
    requestDomoEmbed();
  }, [classId])

  if (redirect) return null;

  const badges = []
  const classId = get(roster, 'ui.selections.classroom.id')
  const schoolId = get(roster, 'ui.selections.school.id')
  for (const student of students) {
    const { studentName, studentId } = student;
    badges.push({
      id: studentId,
      awarder: teacherName,
      badgeTypeId: BADGE_TYPE.CUSTOM,
      description,
      recipient: isClassBadge ? classname : studentName,
      rewardLevelId,
      schoolId: [schoolId],
      classId,
      studentId,
      awardedAt: new Date(),
      locale,
    })
  }

  const localeOptions = cloneDeep(LOCALE_OPTIONS);
  localeOptions.forEach(option => option.label = t(option.label));

  return (
    <React.Fragment>
      <div className='badgeBuilder'>
        <TeacherSelections
          backText={t("BACK TO BADGING REPORT")}
          onBack={props.history.goBack}
          disabled
          teacherSelectionTypes={{
            isAggregateReport: true,
            pageTitle: t("Certificate Builder"),
          }}
        />
        <div className="report-container">
          <div className="scrollContent">
            <div className='section'>
              <div className='sectionInfo'>
                <div className='sectionLabel'>{t("Usage Data")}</div>
              </div>
              <iframe
                srcDoc={domoEmbedSrc}
                scrolling="no"
                className='usageDataEmbed'
              />
            </div>
            <div className='section'>
              <div style={{display: 'flex'}}>
                <div>
                  <div className='sectionInfo'>
                    <div className='sectionLabel'>{t("Certificate Builder")}</div>
                  </div>
                  <div className='certBuilderForm'>
                    <div className='formSection'>
                      <div className='formLabel'>{t("Reward Level")}</div>
                      <Dropdown 
                        type="primary"
                        options={REWARD_LEVEL_OPTIONS[locale]}
                        value={REWARD_LEVEL_OPTIONS[locale].find(x => x.value === rewardLevelId)}
                        onChange={e => setRewardLevelId(e.value)}
                      />
                    </div>
                    <div className='formSection'>
                      <div className='formLabel'>{t("Language")}</div>
                      <Dropdown 
                        type="primary"
                        options={localeOptions}
                        value={LOCALE_OPTIONS.find(x => x.value === locale)}
                        onChange={e => setLocale(e.value)}
                      />
                    </div>
                    {isClassBadge && <div className='formSection'>
                      <div className='formLabel'>{t("Class Name")}</div>
                      <input
                        className='classnameInput'
                        value={classname}
                        onChange={e => setClassname(e.target.value)}
                        maxLength="24"
                      />
                    </div>}
                    <div className='formSection'>
                      <div className='formLabel'>{t("Description")}</div>
                      <textarea 
                        className='descriptionTextArea' 
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        rows="3" 
                        maxLength="100"
                      />
                    </div>
                    <div className="controls">
                      <Button
                        style={{ width: 160 }}
                        onClick={() => setIsPrintModalOpen(true)}
                        waiting={waiting}
                        disabled={!description}
                      >
                        {t("Create Badges")}
                      </Button>
                      <Button type="secondary" onClick={props.history.goBack}>
                        {t("common:Cancel")}
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <Certificate 
                    width={528} 
                    height={408} 
                    baseFontSize={20} 
                    {...badges[0]}
                  />
                  <div className='formLabel' style={{margin: 0, marginTop:4}}>{t("Preview")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CertificatePrintWrapper badges={badges} />
      </div>
      <PrintModal 
        onClose={() => setIsPrintModalOpen(false)}
        onSubmit={onSubmitPrintModal}
        stage={isPrintModalOpen ? 2 : 0}
      />
    </React.Fragment>
  )

  async function onSubmitPrintModal(isColorPrint) {
    setWaiting(true);
    setIsPrintModalOpen(false);

    const { errors } = await PromisePool.for(badges).process(createBadge);
    if (errors.length > 0) console.error('Custom badge creation errors', errors);

    printCertificates(isColorPrint);
    setWaiting(false);
    props.history.push('/teacher/reports/type/badging');
  }

  async function requestDomoEmbed() {
    const filter = [{column: 'classId', operator: 'IN', values: [classId]}]
    const data = await getDomoEmbedSrc('badging', filter);
    setDomoEmbedSrc(data);
  }
}